import React, { useEffect, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { translate } from '../../../../../I18n/i18n';
import './PopUpImages.scss';
import TaskBase from '../../../../ws/TaskBase';

interface PopUpImagesProps extends WithTranslation{
    show: boolean;
    selectedImageIndex: number;
    mediaArray: Array<{ id: number }>;
    onClose: () => void;
}

const PopUpImages: React.FC<PopUpImagesProps> = ({ show, selectedImageIndex, mediaArray, onClose, t }) => {
    const [currentImage, setCurrentImage] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState<number>(selectedImageIndex);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true); // Estado para saber si es la primera carga

    const task = new TaskBase();

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [show]);

    // Este useEffect asegura que cuando el popup se abre, se actualice el índice correctamente
    useEffect(() => {
        if (show) {
            console.log(`PopUp visible, setCurrentIndex: ${selectedImageIndex}`);
            setCurrentIndex(selectedImageIndex); // Establecer el índice inicial
        } else {
            console.log('PopUp hidden, clearing state');
            setCurrentImage(null);
            setLoading(false);
            setError(null);
        }
    }, [show, selectedImageIndex]);

    // Este useEffect solo se activa cuando show es true y currentIndex cambia
    useEffect(() => {
        if (!show) return;

        const fetchImage = async () => {
            setLoading(true);
            setError(null);

            const indexToUse = isFirstLoad ? selectedImageIndex : currentIndex; // Usar selectedImageIndex solo en la primera carga
            console.log(`Fetching image for index: ${indexToUse}, ID: ${mediaArray[indexToUse]?.id}`);

            try {
                const imageBlob = await task.getImage(mediaArray[indexToUse]?.id);
                if (imageBlob) {
                    console.log(`Image fetched successfully for ID: ${mediaArray[indexToUse]?.id}`);
                    const blobURL = URL.createObjectURL(imageBlob);
                    setCurrentImage(blobURL);
                } else {
                    console.error(`Failed to fetch image for ID: ${mediaArray[indexToUse]?.id}`);
                    setError('No se pudo cargar la imagen.');
                }
            } catch (err) {
                console.error('Error while fetching image:', err);
                setError('Error al cargar la imagen.');
            } finally {
                console.log(`Loading finished for index: ${indexToUse}`);
                setLoading(false);
                setIsFirstLoad(false); // Cambiar el estado después de la primera carga
            }
        };

        fetchImage();
    }, [show, currentIndex, mediaArray, isFirstLoad, selectedImageIndex]);

    const handleNext = () => {
        console.log(`Moving to next image from index: ${currentIndex}`);
        setCurrentImage(null);
        setLoading(true); 
        
        setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaArray.length);
    };
    
    const handlePrev = () => {
        console.log(`Moving to previous image from index: ${currentIndex}`);
        setCurrentImage(null); 
        setLoading(true)
  
        setCurrentIndex((prevIndex) => (prevIndex - 1 + mediaArray.length) % mediaArray.length);
    };

    if (!show) return null;

    // Verificar si el array tiene solo una imagen
    const hasMultipleImages = mediaArray?.length > 1;

    return (
        <div className="popup-overlay" onClick={onClose}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="popup-close-btn">
                    <i className="fas fa-times"></i>
                </button>

                <div className="image-gallery">
                    {loading && <div style={{ color: '#fff' }}>{translate(t, 'common.TR_LOADING_LABEL')}</div>}
                    {error && <div>{error}</div>}

                    {currentImage && (
                        <>
                            {hasMultipleImages && (
                                <>
                                    <button
                                        className="popup-nav-btn prev-btn"
                                        onClick={handlePrev}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                </>
                            )}

                            <div className="image-item">
                                <img
                                    className="popup-image"
                                    src={currentImage}
                                    alt={`Image ${mediaArray[currentIndex].id}`}
                                    style={{ objectFit: 'contain' }}
                                />
                            </div>

                            {hasMultipleImages && (
                                <>
                                    <button
                                        className="popup-nav-btn next-btn"
                                        onClick={handleNext}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(PopUpImages);
